import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { config } from "../config";
import { apiClient } from "../lib/api";
import { getUserPreferences, setUserPreferences } from "../lib/user-preferences";
import { useChangePassword } from "./auth-actions/use-change-password";
import { useConfirmAccount } from "./auth-actions/use-confirm-account";
import { useForgotPassword } from "./auth-actions/use-forgot-password";
import { useLogin } from "./auth-actions/use-login";
import { useLogout } from "./auth-actions/use-logout";
import { useRegister } from "./auth-actions/use-register";
import { useResendConfirmation } from "./auth-actions/use-resend-confirmation";
import { useResetPassword } from "./auth-actions/use-reset-password";
import { useUpdateProfile } from "./auth-actions/use-update-profile";

import type { UserPreferences as BackendUserPreferences } from "common/types/user-preferences";
import type { ReactElement, ReactNode } from "react";

import type { AuthContextType, User } from "../types";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactElement => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = !!user;
  const location = useLocation();

  useNavigate();

  useEffect(() => {
    if (config.features.csrfProtection) {
      const loadToken = async (): Promise<void> => {
        const data = await apiClient.fetchCsrfToken();

        apiClient.setCsrfToken(data.csrfToken);
      };

      loadToken();
    }
  }, []);

  useEffect(() => {
    const isUnderAccount = location.pathname.startsWith(`/account`);

    const loadUser = async (): Promise<void> => {
      try {
        if (!isUnderAccount) {
          return;
        }

        const response = await apiClient.accountData();

        if (response.success) {
          const { email, firstName, lastName } = response.details as {
            email: string;
            firstName?: string;
            lastName?: string;
          };

          setUser({ email, firstName, lastName });

          await apiClient.updatePreferences(getUserPreferences());

          const prefsResponse = await apiClient.getPreferences();

          if (prefsResponse.success) {
            const prefs = prefsResponse.details as BackendUserPreferences;

            setUserPreferences(prefs);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [location.pathname]);

  const login = useLogin();

  const logout = useLogout(setUser);

  const register = useRegister();

  const changePassword = useChangePassword();

  const forgotPassword = useForgotPassword();

  const resetPassword = useResetPassword();

  const confirmAccount = useConfirmAccount();

  const resendConfirmation = useResendConfirmation();
  const updateProfile = useUpdateProfile(setUser);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      register,
      changePassword,
      forgotPassword,
      resetPassword,
      confirmAccount,
      resendConfirmation,
      updateProfile,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      register,
      changePassword,
      forgotPassword,
      resetPassword,
      confirmAccount,
      resendConfirmation,
      updateProfile,
    ],
  );

  return createElement(AuthContext.Provider, { value }, children);
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }

  return context;
}
